import { listTargets } from "@/api/client";
import { PageChrome } from "@/components/chrome/PageChrome";
import { GettingStartedWidget } from "@/components/dashboard/GettingStartedWidget";
import { RecentScansPanel } from "@/components/dashboard/RecentScansPanel";
import { TargetsTable } from "@/components/targets/TargetsTable";
import { useScanKickoff } from "@/components/targets/scans/useScanKickoff";
import { RouteShell } from "@/design-system";
import { inFlightInterval } from "@/hooks/useInFlightPolling";
import { useSelectedTierCheckout } from "@/hooks/useSelectedTierCheckout";
import { hasInFlightScan } from "@/lib/scan-status";
import { SELECTED_TIER_SEARCH_SCHEMA } from "@/lib/selectedTier";
import { useOrganization } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/dashboard")({
  // A paid website pricing CTA lands the new account here with ?plan &
  // ?interval (forwarded through Clerk signup). useSelectedTierCheckout
  // consumes them once and kicks off Stripe checkout.
  validateSearch: SELECTED_TIER_SEARCH_SCHEMA,
  component: DashboardPage,
});

function DashboardPage() {
  useSelectedTierCheckout(Route.useSearch());
  const { requestScan, dialog } = useScanKickoff();

  const { data, isPending, error } = useQuery({
    queryKey: ["targets"],
    queryFn: ({ signal }) => listTargets(signal),
    refetchInterval: (q) =>
      inFlightInterval(q.state.data, (d) => d.targets.some(hasInFlightScan)),
  });

  const { organization } = useOrganization();

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <PageChrome crumbs={[]} title={organization?.name} />
      <div className="flex-1 overflow-y-auto flex flex-col gap-5 px-4 sm:px-7 py-6">
        <RouteShell isPending={isPending} error={error}>
          <GettingStartedWidget />
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="min-w-0 lg:basis-1/3 flex flex-col gap-3">
              {data && data.targets.length > 0 ? (
                <TargetsTable
                  targets={data.targets}
                  onScan={requestScan}
                  compact
                />
              ) : null}
            </div>
            {data && data.targets.length > 0 ? (
              <div className="min-w-0 lg:basis-2/3">
                <RecentScansPanel />
              </div>
            ) : null}
          </div>
        </RouteShell>
      </div>
      {dialog}
    </div>
  );
}
